import { useState } from 'react'
import clsx from 'clsx'
import { PlaneLanding, PlaneTakeoff, Play } from 'lucide-react'
import { EmptyState } from '../cc/EmptyState'
import { AMBER, FlapText, GREEN, MUTED, RED, WHITE } from '../cc/splitFlap'
import type { MbmtBoardRow } from './useMbmtApi'

interface RowStatus {
  text: string
  tone: string
  blink?: boolean
  /** The status word is the terminal state (ARRIVED/DEPARTED/DELAYED/...); the flag is
   * its own column now -- how early/late that state landed relative to schedule.
   * Undefined where a delta genuinely isn't known or doesn't apply yet (still-pending
   * states like SCHEDULED/DUE OUT/ON ROUTE/ARRIVING/AT DEPOT/NO SIGNAL). */
  flag?: string
  flagTone?: string
}

/** {flag: "9M LATE"} / {flag: "6M EARLY"} / {flag: "ON TIME"} -- delta is actual minus
 * scheduled, so positive is late and negative is early. deltaMin === 0 still gets a
 * flag ("ON TIME"), unlike the old bare-label behaviour: once there IS a confirmed
 * delta, showing nothing would read as "we don't know", not "spot on". Only a genuinely
 * absent delta (deltaMin == null) returns no flag at all. */
function lateEarlyFlag(deltaMin: number | null): { flag?: string; flagTone?: string } {
  if (deltaMin == null) return {}
  if (deltaMin === 0) return { flag: 'ON TIME', flagTone: GREEN }
  return deltaMin > 0
    ? { flag: `${deltaMin}M LATE`, flagTone: RED }
    : { flag: `${-deltaMin}M EARLY`, flagTone: GREEN }
}

export function statusOf(r: MbmtBoardRow, now: number): RowStatus {
  // No TIMS duty behind this row -- GPS confirmed the movement, but TIMS is the only
  // source of truth for scheduling, so there is nothing to compute early/late against.
  // Must be checked before the delay math below, which assumes a real schedule.
  if (!r.has_duty) return { text: 'NO DUTY', tone: MUTED }
  const dm = Math.round((r.scheduled_ts - now) / 60000)
  if (r.kind === 'departure') {
    if (dm <= 0 && r.on_air && r.moving) return { text: 'DEPARTED', tone: GREEN }
    if (dm <= 0 && r.running_route) return { text: 'ON ROUTE', tone: GREEN }
    // Departed-but-momentarily-paused has to win over the delay clock, same reasoning
    // as arrived-and-parked below: without this, a bus that genuinely left on or near
    // time but is paused between legs (traffic, a signal, a stop) right at the instant
    // the board is queried had no terminal state to land in and kept reading DELAYED,
    // counting up forever. Confirmed live: a bus due out at 15:00 that actually resumed
    // running at 15:38 (38 min late) still read "DELAYED 102M" at 16:43.
    //
    // The delay shown is frozen at scheduled-vs-actual (departed_ts), not scheduled-vs-
    // now, on direct instruction: delay relates to the schedule's own expected time and
    // nothing else, so it must stop moving once the real event is confirmed.
    if (dm < -10 && r.departed) {
      const deltaMin = r.departed_ts != null ? Math.round((r.departed_ts - r.scheduled_ts) / 60000) : null
      return { text: 'DEPARTED', tone: GREEN, ...lateEarlyFlag(deltaMin) }
    }
    // A vehicle with no telemetry at all is a data gap, not a confirmed delay -- "DELAYED"
    // claims we know it should have left and hasn't, which is not true of a device that
    // has never reported. The arrival branch below already draws this line; the departure
    // branch never did, so a bus with a dead/missing tracker blinked red as if it were the
    // slowest bus in the fleet instead of the one we can't see at all.
    if (dm < -10 && !r.on_air) return { text: 'NO SIGNAL', tone: MUTED }
    // Still sitting at the depot past due-out is not the same problem as a bus that's
    // out and unaccounted for -- on direct instruction: it may be held for maintenance,
    // a driver swap, awaiting dispatch, etc, none of which is a schedule failure worth
    // alarming a dispatcher over. Only a bus confirmed AWAY from the depot with no
    // departure evidence earns the red, blinking DELAYED.
    if (dm < -10 && r.at_depot) return { text: 'AT DEPOT', tone: AMBER }
    if (dm < -10) return { text: 'DELAYED', tone: RED, blink: true, flag: `${-dm}M LATE`, flagTone: RED }
    if (dm <= 15) return { text: 'DUE OUT', tone: AMBER }
    return { text: 'SCHEDULED', tone: WHITE }
  }
  if (dm > 20) return { text: r.running_route ? 'ON ROUTE' : 'SCHEDULED', tone: WHITE }
  if (dm >= 0) return { text: 'ARRIVING', tone: GREEN }
  // Arrived-and-parked has to win over the delay clock: without this, a bus that closed
  // its leg exactly on time (or early) and is now correctly idle at the depot had no
  // terminal state to land in and kept reading DELAYED, counting up forever until the
  // row aged out of the board's window. Frozen at scheduled-vs-arrived_ts, same
  // reasoning as the departure branch above -- arrived_ts is absent when arrival was
  // confirmed via at_depot rather than a dated leg, so it just reads ARRIVED plain then.
  if (r.arrived) {
    const deltaMin = r.arrived_ts != null ? Math.round((r.arrived_ts - r.scheduled_ts) / 60000) : null
    return { text: 'ARRIVED', tone: GREEN, ...lateEarlyFlag(deltaMin) }
  }
  if (!r.on_air) return { text: 'NO SIGNAL', tone: MUTED }
  if (dm < -10) return { text: 'DELAYED', tone: RED, blink: true, flag: `${-dm}M LATE`, flagTone: RED }
  return { text: 'ARRIVING', tone: AMBER }
}

function hhmm(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
  })
}

function deltaText(ts: number, now: number): string {
  const dm = Math.round((ts - now) / 60000)
  const abs = Math.abs(dm)
  const t = abs >= 60 ? `${Math.floor(abs / 60)}h ${abs % 60}m` : `${abs}m`
  return dm >= 0 ? `in ${t}` : `${t} ago`
}

const ROW_GRID =
  'grid grid-cols-[128px_54px_116px_84px_minmax(140px,1fr)_130px_92px_150px] items-center gap-x-4'

/**
 * The depot arrival/departure board, styled and animated as a Solari split-flap board --
 * dark leaves, amber captions, characters that mechanically fall into place when their
 * value changes. The theatre is not decoration: a flap ripple is what pulls a desk
 * operator's eye to the one row that just moved, exactly what an airport board is for.
 */
export function MbmtBoard({ rows, now, err, loaded, onSelectVehicle, onPlayDuty, onViewTimeline }: {
  rows: MbmtBoardRow[]
  now: number
  err: string | null
  loaded: boolean
  onSelectVehicle: (id: string) => void
  /** Optional: a play-duty affordance on each row, next to the status flap. Omitted
   *  entirely (no button rendered) when the caller has nowhere to put the playback UI. */
  onPlayDuty?: (id: string) => void
  /** Optional: opens this vehicle's full day of stops/trips. Fired from the same row
   *  click that also fires onSelectVehicle -- the row is now the single control for
   *  both "jump to this vehicle on the Live map" and "show its trip timeline". */
  onViewTimeline?: (id: string) => void
}): JSX.Element {
  const [filter, setFilter] = useState<'all' | 'arrival' | 'departure'>('all')
  const shown = rows.filter((r) => filter === 'all' || r.kind === filter)
  const arrivals = rows.filter((r) => r.kind === 'arrival').length
  const departures = rows.length - arrivals

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[18px] border border-[rgba(15,23,42,0.2)] bg-[#0B1220] shadow-[0_18px_44px_-24px_rgba(2,6,23,0.9)]">
      <header className="flex shrink-0 items-center gap-4 border-b border-white/[0.07] px-5 pb-3.5 pt-4">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-[#FBBF24]">
            Mira Bhayandar · depot board
          </p>
          <p className="mt-1 text-[11px] text-[#64748B]">
            TIMS schedule · live telemetry · trip legs
          </p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex gap-1">
            {([['all', 'All'], ['arrival', `Arrivals ${arrivals}`], ['departure', `Departures ${departures}`]] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setFilter(id)}
                className={clsx(
                  'rounded-full px-3 py-1 text-[11px] font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]',
                  filter === id
                    ? 'bg-[#FBBF24] text-[#0B1220]'
                    : 'text-[#94A3B8] hover:bg-white/[0.06] hover:text-[#E8EDF6]',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <FlapText
            text={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
            width={8}
            tone={AMBER}
          />
        </div>
      </header>

      <div className={clsx(ROW_GRID, 'shrink-0 border-b border-white/[0.07] px-5 py-2')}>
        {['Scheduled', '', 'Vehicle', 'Schedule', 'Driver', 'Status', 'Flag', 'SoC → arrival'].map((h, i) => (
          <span key={i} className="font-mono text-[9.5px] font-medium uppercase tracking-[0.18em] text-[#526079]">
            {h}
          </span>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-2 py-1">
        {!loaded && (
          <div className="space-y-2 p-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-11 animate-pulse rounded-[10px] bg-white/[0.05]" />
            ))}
          </div>
        )}
        {loaded && shown.length === 0 && (
          <div className="[&_*]:!text-[#94A3B8]">
            <EmptyState
              title={err ? 'TIMS schedule unreachable' : 'No movements on the board'}
              body={err
                ? `Duty feed error: ${err}. The board fills as soon as TIMS answers.`
                : 'Nothing scheduled out of or back to the depot in the board window.'}
            />
          </div>
        )}
        {shown.map((r) => {
          const st = statusOf(r, now)
          const overdue = r.scheduled_ts < now - 5 * 60_000 && st.tone === RED
          return (
            <div key={`${r.vehicle_id}-${r.kind}-${r.scheduled_ts}`} className="group relative">
            <button
              type="button"
              onClick={() => { onSelectVehicle(r.vehicle_id); onViewTimeline?.(r.vehicle_id) }}
              className={clsx(
                ROW_GRID,
                'w-full animate-row-in rounded-[10px] px-3 py-2 text-left transition-colors',
                'border-b border-white/[0.04] last:border-b-0',
                'hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]',
                overdue && 'bg-[rgba(248,113,113,0.05)]',
              )}
            >
              <span className="flex items-center gap-2.5">
                <FlapText text={hhmm(r.scheduled_ts)} width={5} />
                <span className={clsx(
                  'text-[10px] font-medium tabular-nums',
                  overdue ? 'text-[#F87171]' : 'text-[#64748B]',
                )}>
                  {deltaText(r.scheduled_ts, now)}
                </span>
              </span>
              <span className={clsx(
                'flex h-[22px] w-[46px] items-center justify-center gap-1 rounded-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em]',
                r.kind === 'arrival'
                  ? 'bg-[rgba(52,211,153,0.12)] text-[#34D399]'
                  : 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]',
              )}>
                {r.kind === 'arrival' ? <PlaneLanding size={10} /> : <PlaneTakeoff size={10} />}
                {r.kind === 'arrival' ? 'IN' : 'OUT'}
              </span>
              <span className="font-mono text-[13px] font-semibold tracking-tight text-[#E8EDF6]">
                {r.vehicle_id}
              </span>
              <span className="font-mono text-[13px] font-semibold text-[#FBBF24]">
                {r.schedule_code || '—'}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[12px] font-medium text-[#C7D0DE]">
                  {r.driver_name || 'no duty crew on record'}
                </span>
                <span className="block truncate text-[10px] text-[#64748B]">
                  {r.running_route || r.route_code || 'no route on record'}
                  {r.running_direction ? ` · ${r.running_direction.toUpperCase()}` : ''}
                  {r.schedule_shift ? ` · ${r.schedule_shift}` : ''}
                  {r.legs_done > 0 ? ` · ${r.legs_done} leg${r.legs_done === 1 ? '' : 's'} done` : ''}
                  {r.kind === 'arrival' && !r.arrived && r.eta_depot_traffic_min != null
                    ? (r.eta_depot_source === 'haversine_fallback'
                      ? ` · ETA ~${r.eta_depot_traffic_min}m`
                      : ` · traffic ETA ${r.eta_depot_traffic_min}m`)
                    : ''}
                </span>
              </span>
              <span className={clsx(st.blink && 'animate-pulse-dot')}>
                <FlapText text={st.text} width={11} tone={st.tone} small stagger={18} />
              </span>
              <span>
                {st.flag ? (
                  <FlapText text={st.flag} width={9} tone={st.flagTone ?? MUTED} small stagger={18} />
                ) : (
                  <span className="font-mono text-[11px] text-[#3B4863]">—</span>
                )}
              </span>
              <span className="font-mono text-[12px] tabular-nums">
                {r.soc_pct != null ? (
                  <>
                    <span className={clsx(
                      r.soc_pct < 20 ? 'text-[#F87171]' : r.soc_pct < 35 ? 'text-[#FBBF24]' : 'text-[#34D399]',
                    )}>
                      {Math.round(r.soc_pct)}%
                    </span>
                    {r.kind === 'arrival' && (
                      r.expected_soc_pct != null ? (
                        <span
                          title={`${r.soc_basis}${r.soc_rate_pct_per_hr != null ? ` · ${r.soc_rate_pct_per_hr}%/h` : ''}`}
                          className={clsx(
                            'ml-1',
                            r.expected_soc_pct < 20 ? 'text-[#F87171]' : r.expected_soc_pct < 35 ? 'text-[#FBBF24]' : 'text-[#7E93B4]',
                          )}
                        >
                          → ~{Math.round(r.expected_soc_pct)}%
                          <span className="ml-0.5 text-[9px] uppercase text-[#526079]">est</span>
                        </span>
                      ) : (
                        <span title={r.soc_basis} className="ml-1 text-[10px] text-[#526079]">
                          no est
                        </span>
                      )
                    )}
                  </>
                ) : (
                  <span className="text-[#526079]">—</span>
                )}
              </span>
            </button>
            {onPlayDuty && (
              <button
                type="button"
                title="Play this duty"
                onClick={(e) => { e.stopPropagation(); onPlayDuty(r.vehicle_id) }}
                className="absolute right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-[#E8EDF6] hover:bg-white/20 group-hover:flex"
              >
                <Play size={10} />
              </button>
            )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
